import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  ClockCircleOutlined,
  CloseCircleTwoTone,
  CloseOutlined,
  LoadingOutlined,
  WarningOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import { Alert, Form, Tooltip } from 'antd';
import { isString, memoize, uniq } from 'lodash';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { areEqual } from 'react-window';
import { useTheme } from 'styled-components';

import { None } from 'components/data/helpers';
import { DRAG_AND_DROP_DIRECTORY_ERROR, useUploadContext } from 'components/FileUploader';
import { EditAttachmentForm } from 'components/RecordAttachments/EditAttachmentModal/EditAttachmentForm';
import { useUploadInspectionDataModalContext } from 'horizon/routes/Inspections/InspectionsUploadManager/Context/UploadInspectionDataModalContext';
import { ValidationTable } from 'horizon/routes/Inspections/InspectionsUploadManager/UploadInspectionDataModal.style';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlInspectionDraftImage,
  type Maybe,
  AtlasGqlAttachmentRecordType,
} from 'types/atlas-graphql';
import { STANDARD_COLUMN_SCROLL_Y } from 'utils/constants';

import { type FilteredFiles, type UploadFile } from '../../types';
import { FileUploadProgressSummary } from '../FileUploadProgressSummary';

import {
  ErrorWrapper,
  FilesSelected,
  SelectedFile,
  StyledFilename,
  StyledFileNameRow,
  StyledList,
  StyledRemoveButton,
} from './SelectedFileList.style';

const DEFAULT_ITEM_SIZE = 35;
const TALL_ITEM_SIZE = 200;

interface Data {
  fileName: string;
  listRef: React.Ref<any>;
  onRemove: (name: string) => void;
  selectedFiles: UploadFile[];
  setSelectedFiles: any;
  uploading?: boolean;
  validFilenames: string[];
  displayAttachmentOptions?: boolean;
  attachmentRecordParentType?: AtlasGqlAttachmentRecordType;
}

interface SelectedFileItemProps {
  data: Data;
  index: number;
  style?: React.CSSProperties;
}

type MatchStatus = {
  status: 'success' | 'warning' | 'error';
  message?: string;
};

const SelectedFileItem = memo(({ data, index, style }: SelectedFileItemProps) => {
  const theme = useTheme();

  const {
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    onRemove,
    listRef,
    displayAttachmentOptions = false,
    attachmentRecordParentType = AtlasGqlAttachmentRecordType.Other,
  } = data;
  const file = selectedFiles[index];
  const { status } = file;

  const loadingIcon = <LoadingOutlined style={{ color: theme.teal }} spin />;
  const completeIcon = <CheckCircleOutlined style={{ color: theme.green }} />;
  const warningIcon = <WarningOutlined style={{ color: theme.red }} />;
  const pendingIcon = <ClockCircleOutlined />;

  // Callback to handle mapping the form values back to the files
  // so that they can be passed with the file data to the mutation call.
  const handleAttachmentValuesChange = () => {
    const values = form.getFieldsValue();
    if (setSelectedFiles) {
      const tempSelectedFiles = [...selectedFiles];
      tempSelectedFiles[index] = { ...file, ...values };
      setSelectedFiles(tempSelectedFiles);
    }
  };

  // Instantiate the form
  const [form] = Form.useForm();

  return (
    <SelectedFile key={file.sourceName + index} style={style}>
      <StyledFileNameRow>
        <StyledFilename $invalid={!validFilenames.includes(file.sourceName)}>
          {<b>{file.sourceName}</b>}
        </StyledFilename>
        {uploading ? (
          status === 'error' ? (
            warningIcon
          ) : status === 'completed' ? (
            completeIcon
          ) : status === 'uploading' ? (
            loadingIcon
          ) : (
            pendingIcon
          )
        ) : (
          <StyledRemoveButton
            type="link"
            danger={true}
            value={file.sourceName}
            icon={<CloseOutlined />}
            onClick={(e) => {
              onRemove((e.currentTarget as HTMLButtonElement).value);
              (listRef as any)?.current?.resetAfterIndex(index, false);
            }}
          />
        )}
      </StyledFileNameRow>
      {displayAttachmentOptions && (
        <EditAttachmentForm
          form={form}
          handleChange={handleAttachmentValuesChange}
          categoryOnChange={handleAttachmentValuesChange}
          parentType={attachmentRecordParentType}
          disabled={uploading || !file.valid}
          /**
           * Pull default values from selectedFiles data. Initially these will be
           * undefined, but this prevents fields from appearing empty after
           * scrolling around the virtualized list and unmounting this component.
           */
          defaultCategoryId={selectedFiles[index].category}
          defaultDescription={selectedFiles[index].description}
        />
      )}
    </SelectedFile>
  );
}, areEqual);

// Memoized wrapper around the set of data that each list item will need
const createItemData = memoize(
  (
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType
  ) => ({
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType,
  })
);

export interface SelectedFileListProps {
  errorMessage?: React.ReactNode;
  fileName: string;
  filteredFiles: FilteredFiles;
  corruptFiles: string[];
  existingDraftImages?: AtlasGqlInspectionDraftImage[] | null;
  onRemove: (name: string) => void;
  showErrorSummary?: boolean;
  uploading?: boolean;
  validFiles: UploadFile[];
  displayAttachmentOptions?: boolean;
  attachmentRecordParentType?: AtlasGqlAttachmentRecordType;
  showDragAndDropDirectoryError?: boolean;
  height?: number;
  // Set width to null if the list should take up the full space provided.
  width?: number | null;
}

export function SelectedFileList({
  errorMessage,
  fileName,
  filteredFiles,
  corruptFiles = [],
  existingDraftImages,
  onRemove,
  showErrorSummary,
  uploading,
  validFiles,
  displayAttachmentOptions = false,
  attachmentRecordParentType,
  showDragAndDropDirectoryError,
  height = 330,
  width = 496,
}: SelectedFileListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { selectedFiles, setSelectedFiles } = useUploadContext();
  const validFilesCount = validFiles.length;
  const validFilenames = validFiles.map((file) => file.sourceName);
  const invalidCount = selectedFiles.length - validFilesCount;

  const { imageDetailsContext } = useUploadInspectionDataModalContext();
  const {
    previewData: imageDetailsPreviewData,
    imagePathFieldName,
    setSomeImagesMatchingMetadata,
  } = imageDetailsContext;

  function itemKey(index: number, data: { selectedFiles: UploadFile[] }) {
    // Find the item at the specified index.
    // In this case "data" is an Array that was passed to List as "itemData".
    const itemKey = data?.selectedFiles[index].sourceName;

    // Return a value that uniquely identifies this item.
    // Typically this will be a UID of some sort.
    return itemKey;
  }

  const listRef = useRef<any>();

  const itemData = createItemData(
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType
  );

  const finalInvalidCount =
    corruptFiles.length === 0 ? invalidCount : invalidCount - corruptFiles.length;

  const previewTableProps: {
    columns: TTableColumnDef[];
    dataSource: (Partial<UploadFile> & {
      matchedDetailsPath: string | undefined;
      matchStatus: MatchStatus;
    })[];
  } | null = useMemo(() => {
    if (!imageDetailsPreviewData?.length) {
      return null;
    }

    const columns: TTableColumnDef[] = [
      {
        key: 'uploadedFile',
        title: t('table.uploadedFile', 'Uploaded File'),
        dataIndex: 'name',
        ellipsis: { showTitle: true },
        render: (value) => value ?? <None />,
      },
      {
        key: 'matchedDetailsPath',
        title: t('table.matchedDetailsPath', 'Matched Details Path'),
        dataIndex: 'matchedDetailsPath',
        ellipsis: { showTitle: true },
        render: (value) => value ?? <None />,
      },
      {
        key: 'matchStatus',
        title: t('table.matchSuccess', 'Match Success'),
        dataIndex: 'matchStatus',
        align: 'center',
        width: 140,
        render: ({ status, message }: MatchStatus) => {
          return status === 'success' ? (
            <CheckCircleTwoTone twoToneColor={theme.green} />
          ) : status === 'warning' ? (
            <Tooltip title={message}>
              <WarningTwoTone twoToneColor={theme.yellow} />
            </Tooltip>
          ) : (
            <Tooltip title={message}>
              <CloseCircleTwoTone twoToneColor={theme.red} />
            </Tooltip>
          );
        },
      },
      {
        key: 'remove',
        title: t('common.remove', 'Remove'),
        dataIndex: 'sourceName',
        align: 'center',
        width: 84,
        render: (value, record) => {
          if (value) {
            return (
              <>
                {uploading ? (
                  record.status === 'error' ? (
                    <WarningOutlined style={{ color: theme.red }} />
                  ) : record.status === 'completed' ? (
                    <CheckCircleOutlined style={{ color: theme.green }} />
                  ) : record.status === 'uploading' ? (
                    <LoadingOutlined style={{ color: theme.teal }} spin />
                  ) : (
                    <ClockCircleOutlined />
                  )
                ) : (
                  <StyledRemoveButton
                    type="link"
                    danger={true}
                    icon={<CloseOutlined />}
                    onClick={() => {
                      onRemove(value);
                    }}
                  />
                )}
              </>
            );
          }

          // No remove button for rows with missing files or existing draft images
          return null;
        },
      },
    ];

    // Helper to get lowercase string values for case-insensitive matching
    const getSafeLowercase = (val: number | Maybe<string> | undefined) => {
      return isString(val) ? val.toLowerCase() : val;
    };

    const allFileNames = uniq([
      ...selectedFiles.map(({ name }) => getSafeLowercase(name)),
      ...(existingDraftImages ?? []).map(({ path }) => getSafeLowercase(path)),
      ...imageDetailsPreviewData.map((row) => getSafeLowercase(row[imagePathFieldName])),
    ]);

    const dataSource = allFileNames.reduce<
      (Partial<UploadFile> & {
        matchedDetailsPath: string | undefined;
        matchStatus: MatchStatus;
      })[]
    >((acc, name) => {
      const safeName = getSafeLowercase(name);
      const selectedFile = selectedFiles.find((f) => getSafeLowercase(f.name) === safeName);
      const existingDraftImage = existingDraftImages?.find(
        (i) => getSafeLowercase(i.path) === safeName
      );
      const matchedDetails = imageDetailsPreviewData.find(
        (row) => getSafeLowercase(row[imagePathFieldName]) === safeName
      );

      return [
        ...acc,
        ...(selectedFile
          ? [
              {
                ...selectedFile,
                matchedDetailsPath: matchedDetails
                  ? String(matchedDetails[imagePathFieldName])
                  : undefined,
                matchStatus: (!!matchedDetails
                  ? {
                      status: 'success',
                    }
                  : {
                      status: 'warning',
                      message: t(
                        'components.imageNotInDetailsFileNotUploaded',
                        'This image does not exist in the details file. It will not be uploaded.'
                      ),
                    }) as MatchStatus,
              },
            ]
          : []),
        ...(existingDraftImage
          ? [
              {
                name: `${existingDraftImage.path} ${t('components.existingSuffix', '(existing)')}`,
                matchedDetailsPath: matchedDetails
                  ? String(matchedDetails[imagePathFieldName])
                  : undefined,
                matchStatus: (!!matchedDetails
                  ? {
                      status: 'success',
                    }
                  : {
                      status: 'warning',
                      message: t(
                        'components.imageNotInDetailsFileNoMetadata',
                        'This image does not exist in the details file. Metadata will not be applied.'
                      ),
                    }) as MatchStatus,
              },
            ]
          : []),
        ...(!selectedFile && !existingDraftImage
          ? [
              {
                matchedDetailsPath: matchedDetails
                  ? String(matchedDetails[imagePathFieldName])
                  : undefined,
                matchStatus: {
                  status: 'error',
                  message: t(
                    'components.noImageMapsToMetadata',
                    'No image maps to this metadata. It will not be applied.'
                  ),
                } as MatchStatus,
              },
            ]
          : []),
      ];
    }, []);

    return { columns, dataSource };
  }, [imageDetailsPreviewData, selectedFiles, existingDraftImages, onRemove, theme, t]);

  useEffect(() => {
    if (imageDetailsPreviewData.length) {
      const hasMatch = !!previewTableProps?.dataSource.some(
        (row) => row.matchStatus.status === 'success'
      );
      setSomeImagesMatchingMetadata(hasMatch);
    }
  }, [previewTableProps, imageDetailsPreviewData]);

  return (
    <>
      {showErrorSummary ? (
        <ErrorSummary validFilesCount={validFilesCount} />
      ) : (
        <>
          {errorMessage && <Alert type="error" message={errorMessage} />}

          {uploading ? (
            <FileUploadProgressSummary />
          ) : (
            <>
              {corruptFiles.length > 0 && (
                <Alert
                  type="warning"
                  message={t('components.corruptFilesWarning', {
                    defaultValue_one: '{{count}} file will not be uploaded because it is corrupt.',
                    defaultValue_other:
                      '{{count}} files will not be uploaded because they are corrupt.',
                    count: corruptFiles.length,
                  })}
                />
              )}
              {finalInvalidCount > 0 && (
                <Alert
                  type="warning"
                  message={t('components.invalidFilesWarning', {
                    defaultValue_one: '{{count}} file will not be uploaded because it is invalid.',
                    defaultValue_other:
                      '{{count}} files will not be uploaded because they are invalid.',
                    count: finalInvalidCount,
                  })}
                />
              )}
              {filteredFiles.status && (
                <Alert
                  type="warning"
                  message={t('components.dsStoreFilteredWarning', {
                    defaultValue_one:
                      '{{count}} .DS_Store file has been filtered out because it is not necessary',
                    defaultValue_other:
                      '{{count}} .DS_Store files have been filtered out because they are not necessary',
                    count: filteredFiles.count,
                  })}
                />
              )}
              {showDragAndDropDirectoryError && (
                <Alert type="warning" message={DRAG_AND_DROP_DIRECTORY_ERROR} />
              )}
              <FilesSelected>
                {selectedFiles.length}{' '}
                {selectedFiles.length === 1
                  ? t('components.fileSelected', 'file selected')
                  : t('components.filesSelected', 'files selected')}
              </FilesSelected>
            </>
          )}

          {previewTableProps ? (
            <ValidationTable {...previewTableProps} scroll={{ y: STANDARD_COLUMN_SCROLL_Y.y }} />
          ) : (
            <StyledList
              itemCount={selectedFiles.length}
              // The itemSize needs to be taller if displaying the Attachment details sub-form
              itemSize={() => (displayAttachmentOptions ? TALL_ITEM_SIZE : DEFAULT_ITEM_SIZE)}
              itemData={itemData}
              height={height}
              width={width}
              itemKey={itemKey}
              ref={listRef}
            >
              {SelectedFileItem as any}
            </StyledList>
          )}
        </>
      )}
    </>
  );
}

interface ErrorSummaryProps {
  validFilesCount: number;
}

function ErrorSummary({ validFilesCount }: ErrorSummaryProps) {
  const { t } = useTranslation();
  const { selectedFiles } = useUploadContext();
  const errors = selectedFiles.filter(({ status }) => status === 'error');

  return (
    <>
      <p>
        {t('components.filesUploadedSuccessfully', {
          defaultValue_one: '{{count}} file uploaded successfully.',
          defaultValue_other: '{{count}} files uploaded successfully.',
          count: validFilesCount - errors.length,
        })}
      </p>
      <ErrorWrapper>
        <p>{t('components.followingFilesNotUploaded', 'The following files were not uploaded:')}</p>
        <ul>
          {errors.map((file) => (
            <li key={file.sourceName}>{file.sourceName}</li>
          ))}
        </ul>
      </ErrorWrapper>
    </>
  );
}
